import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { cloneDeep } from 'lodash';
import StrategyCalculation from 'scaleflow/components/strategy/StrategyCalculation';
import {
  objectExtract,
  sortInitiatives,
} from 'scaleflow/components/initiatives/table/index';
import { _labelExtract as labelExtract } from 'scaleflow/helpers/label-extract';
import capitalize from 'scaleflow/utils/capitalize';
import RoadmapValidation from 'scaleflow/validators/roadmap';

export default class RoadmapFormIndexComponent extends StrategyCalculation {
  constructor() {
    super(...arguments);

    const { selectedScenario } = this.args;
    this.selectedScenario = selectedScenario;
    this.generateRows();
    this.generateSelectedRows();
  }

  roadmapValidation = RoadmapValidation;

  @service betaToggle;
  @service('themes') themesService;
  @service users;
  @service('teams') teamsService;
  @service intl;
  @service store;
  @service router;
  @service('roadmap') roadmapService;

  @tracked selectedRows = [];
  @tracked selectedInitiatives = this.roadmap.initiatives?.content ?? [];
  @tracked rows = [];
  @tracked selectedScenario = null;

  get initiatives() {
    return this.args.initiatives;
  }

  get roadmapInitiatives() {
    return this.selectedInitiatives ?? [];
  }

  get scenario() {
    return this.roadmap['investment-scenario'];
  }

  get roadmap() {
    return this.args.roadmap;
  }

  get statusOptions() {
    return this.betaToggle.statusOptions;
  }

  get sourceOptions() {
    return this.betaToggle.sourceOptions;
  }

  get themeOptions() {
    return this.themesService.themeOptions;
  }

  get pmOptions() {
    return this.users.userOptions;
  }

  get teamOptions() {
    return this.teamsService.teamOptions;
  }

  get groupingOptions() {
    return this.betaToggle.groupingOptions;
  }

  get groupingLabel() {
    return this.grouping
      ? this.intl.t('dictionary.group-by') +
          ': ' +
          this.groupingOptions.filter(
            (item) => item.value === this.grouping,
          )?.[0]?.label
      : this.intl.t('dictionary.group-by');
  }

  get grouping() {
    return this.args.grouping;
  }

  get expected_investment() {
    return this.selectedInitiatives.reduce(
      (acc, item) => Number(acc) + Number(item.bon_investment ?? 0),
      0,
    );
  }

  get expected_return() {
    return this.selectedInitiatives.reduce(
      (acc, item) => Number(acc) + Number(item.bon_returns ?? 0),
      0,
    );
  }

  get expected_value() {
    return this.selectedInitiatives.reduce(
      (acc, item) => Number(acc) + Number(item.bon_value ?? 0),
      0,
    );
  }

  @action
  setSelectedRows(rows) {
    this.selectedRows = rows;
  }

  @action
  extractAndSetSelectedInitiatives(changeset, rows) {
    const selectedInitiatives = [];
    rows.forEach((item) => {
      if (item?.children) selectedInitiatives.splice(-1, 0, ...item.children);
      else selectedInitiatives.push(item);
    });
    this.setSelectedInitiatives(
      this.getInitiativeRecordsFromIds(
        selectedInitiatives.map((item) => item.id),
      ),
      changeset,
    );
  }

  @action
  setSelectedInitiatives(initiatives) {
    this.selectedInitiatives = initiatives;
    this.generateRows();
    // if (changeset) changeset.set('initiatives', initiatives);
  }

  getInitiativeRecordsFromIds(ids) {
    return ids.map((id) => {
      const initiative = this.store.peekRecord('initiative', id);
      return initiative;
    });
  }

  @action
  openSlideover(initiative) {
    this.initiativeSlideover.open(initiative.id);
  }

  get selectedInitiativeIds() {
    return this.roadmapInitiatives.map((item) => item.id);
  }

  @action
  updateSelectedInitiatives(changeset, e) {
    const id = e.value;
    const existing_ids = this.selectedInitiatives.map((item) => item.id);
    existing_ids.push(id);
    const unique_ids = Array.from(new Set(existing_ids));
    const initiatives = this.getInitiativeRecordsFromIds(unique_ids);
    this.setSelectedInitiatives(initiatives, changeset);
  }

  @action
  removeInitiative(changeset, id) {
    const ids = this.selectedInitiativeIds.filter((_id) => _id !== id);
    const initiatives = this.getInitiativeRecordsFromIds(ids);
    this.setSelectedInitiatives(initiatives, changeset);
  }

  @action
  setScenario(changeset, e) {
    const scenario = this.store.peekRecord('investment-scenario', e.value);
    this.selectedScenario = scenario;
    changeset.set('investment-scenario', scenario);
  }

  @action
  setStartDate(changeset, e) {
    changeset.set('start_date', e[0]);
  }

  @action
  setEndDate(changeset, e) {
    changeset.set('end_date', e[0]);
  }

  @action
  async saveRoadmap(changeset) {
    changeset.set('investment-scenario', this.selectedScenario);
    changeset.set('initiatives', this.selectedInitiatives);
    await this.selectedScenario.save();
    changeset
      .save()
      .then((val) => {
        this.roadmapService.setId(val.id);
        this.router.transitionTo('planning.dashboard', {
          queryParams: {
            roadmap: val.id,
          },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }

  @action
  generateRows() {
    let rows = [];
    if (this.grouping) {
      const groupedInitiatives = this.getGroupedInitiativesByKey(
        this.initiatives,
        this.grouping,
      );
      rows = Object.keys(groupedInitiatives).map((key) => {
        const value = groupedInitiatives[key];
        return {
          title: value.title,
          selected: undefined,
          children: value.content.map((item) =>
            this.convertInitiativeToRowItem(item),
          ),
        };
      });
    } else {
      rows = this.initiatives.map((item) =>
        this.convertInitiativeToRowItem(item),
      );
    }
    this.rows = rows;
  }

  getGroupedInitiativesByKey(list, groupKey) {
    switch (groupKey) {
      case 'status': {
        return sortInitiatives(
          list,
          'No Status',
          'status',
          'status',
          (value) => objectExtract(this.betaToggle.statusOptions, value)?.label,
          'tableOrder',
        );
      }
      case 'source': {
        return sortInitiatives(
          list,
          'No Source',
          'source',
          'source',
          (value) => objectExtract(this.betaToggle.sourceOptions, value)?.label,
          'tableOrder',
        );
      }
      case 'product-manager': {
        return sortInitiatives(
          list,
          'No PM',
          'productManager.id',
          'productManager.name',
        );
      }
      case 'team': {
        return sortInitiatives(
          list,
          'No Team',
          'firstTeam.id',
          'firstTeam.name',
        );
      }
      case 'theme': {
        return sortInitiatives(list, 'No Theme', 'theme.id', 'theme.title');
      }
      case 'updates': {
        return sortInitiatives(
          list,
          'No Update',
          'latestUpdate.status',
          'latestUpdate.status',
          (val) => this.intl.t(`_initiatives.updates_${val?.toLowerCase()}`),
        );
      }

      default: {
        return sortInitiatives(list, 'No Status', 'status', 'status');
      }
    }
  }

  //create object with key intiiative id and value true or false from selectedInitiatives array
  get selectedInitiativesMap() {
    return this.selectedInitiatives.reduce((acc, item) => {
      acc[item.id] = true;
      return acc;
    }, {});
  }

  convertInitiativeToRowItem(initiative) {
    initiative.recommended = {
      recommended: this.isInitiativeRecommended(initiative),
    };
    return {
      title: initiative.title,
      recommended: initiative.recommended,
      starting_week: initiative.startingWeek,
      duration: initiative.duration ? initiative.duration + ' weeks' : '-',
      source: capitalize(
        initiative.source
          ? labelExtract(
              this.betaToggle.sourceOptions,
              initiative.source,
            ).split(' ')?.[0]
          : `${this.intl.t('dictionary.no')} ${this.intl
              .t('dictionary.source', {
                num: 1,
              })
              .toLowerCase()}`,
      ),
      status: capitalize(
        initiative.status
          ? labelExtract(this.betaToggle.statusOptions, initiative.status)
          : `${this.intl.t('dictionary.no')} ${this.intl.t('dictionary.status').toLowerCase()}`,
      ),
      person_weeks: initiative.person_weeks_excluding_uncertainty
        ? initiative.person_weeks_excluding_uncertainty + ' pw'
        : '-',
      uncertainty: initiative.person_weeks_only_uncertainty
        ? initiative.person_weeks_only_uncertainty + ' pw'
        : '-',
      priority: initiative.priority ?? '-',
      bon: initiative.localizedBonValue,
      id: initiative.id,
      selected: {
        selected: this.selectedInitiativesMap[initiative.id],
        onChange: (checked) => this.handleInitiativeToggle(checked, initiative),
      },
    };
  }

  @action
  handleInitiativeToggle(checked, initiative) {
    if (checked) this.addInitiativeToRoadmap(initiative);
    else this.removeInitiativeFromRoadmap(initiative);
  }

  isInitiativeRecommended(initiative) {
    const scenarioPWLimits = this.scenarioPWLimits;
    const source = initiative.source?.toLowerCase();
    if (!source || source === 'other') return false;
    if (!initiative.person_weeks_including_uncertainty) return false;
    if (
      +initiative.person_weeks_including_uncertainty <
        scenarioPWLimits[source].lower_limit ||
      +initiative.person_weeks_including_uncertainty >
        scenarioPWLimits[source].upper_limit
    )
      return false;

    return true;
  }

  @action
  generateSelectedRows() {
    const rows = cloneDeep(this.rows); // cloneDeep is used to avoid mutating the original array (rows
    const selectedInitiativesIds = this.selectedInitiatives.map(
      (item) => item.id,
    );
    const selectedRows = [];
    rows.forEach((row) => {
      const children = row.children;
      if (children) {
        const selectedChildren = children.filter((item) =>
          selectedInitiativesIds.includes(item.id),
        );
        if (selectedChildren.length > 0) {
          const newRow = row;
          newRow.children = selectedChildren;
          selectedRows.push(newRow);
        }
      } else {
        if (selectedInitiativesIds.includes(row.id)) {
          selectedRows.push(row);
        }
      }
    });
    return selectedRows;
  }

  @action
  onSelectedInitiativesUpdate() {
    this.generateSelectedRows();
  }

  @action
  selectionMatchFunction(_, rowValue) {
    return this.selectedInitiativeIds.includes(rowValue.id);
  }

  get recommended_initiatives() {
    return this.initiatives.filter((item) => item.recommended?.recommended);
  }

  initiativesAreUnchanged(changeset_initiative_ids, selected_initiative_ids) {
    return (
      changeset_initiative_ids.length === selected_initiative_ids.length &&
      changeset_initiative_ids.every((id) =>
        selected_initiative_ids.includes(id),
      )
    );
  }

  @action
  changesetIsInvalidOrUntouched(changeset) {
    if (!changeset.title || !changeset.title.trim().length) return true;
    if (changeset.isInvalid) return true;
    let initiatives = changeset.get('initiatives');
    if ('promise' in initiatives)
      initiatives = (initiatives?.content ?? []).map((item) => item.id);
    const sameInitiatives = this.initiativesAreUnchanged(
      initiatives.map((item) => item.id),
      this.selectedInitiativeIds,
    );
    if (
      changeset.title === this.roadmap.title &&
      changeset.get('investment-scenario.id') ===
        this.roadmap.get('investment-scenario.id') &&
      changeset?.start_date === this.roadmap?.start_date &&
      changeset?.end_date === this.roadmap?.end_date &&
      sameInitiatives
    )
      return true;
  }

  @action
  addInitiativeToRoadmap(initiative) {
    const id = initiative.id;
    const existing_ids = this.selectedInitiatives.map((item) => item.id);
    existing_ids.push(id);
    const unique_ids = Array.from(new Set(existing_ids));
    const initiatives = this.getInitiativeRecordsFromIds(unique_ids);
    this.setSelectedInitiatives(initiatives);
  }

  @action
  removeInitiativeFromRoadmap(initiative) {
    const id = initiative.id;
    const filtered_out_ids = this.selectedInitiatives
      .filter((item) => item.id !== id)
      .map((item) => item.id);
    const unique_ids = Array.from(new Set(filtered_out_ids));
    const initiatives = this.getInitiativeRecordsFromIds(unique_ids);
    this.setSelectedInitiatives(initiatives);
  }

  @action
  cancelForm(changeset) {
    if (!changeset.id) this.roadmap.deleteRecord();
    this.router.transitionTo('planning.dashboard');
  }

  @action
  deleteRoadmap() {
    /**
     * Delete linked scenario if connected to this roadmap
     */
    const roadmaps_linked_to_scenario = this.selectedScenario?.roadmaps ?? [];
    if (roadmaps_linked_to_scenario.length === 1) {
      this.selectedScenario.deleteRecord();
    }
    this.roadmap.destroyRecord().then(() => {
      this.router.replaceWith('planning.dashboard');
    });
  }

  @action
  handleScenarioAssign(e) {
    const id = e.value;
    const scenario = this.store.peekRecord('investment-scenario', id);
    const roadmap = this.roadmap;
    roadmap['investment-scenario'] = scenario;
    roadmap.save().then(() => {
      this.selectedScenario = scenario;
    });
  }
}
